import {
  BodyText,
  Box,
  CheckboxGroup,
  CheckboxTile,
  Flex,
  Heading,
  TextInput,
} from "@utilitywarehouse/hearth-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PATHS } from "../../config/routeConfig";
import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import NavigationButtons from "../../components/Questions/NavigationButtons";
import { useAuthContext } from "../../context/auth/useAuthContext";
import { checkTokenExpiryOnInterval } from "../../context/auth/Token";
import Unauthenticated from "../../components/Unauthenticated";
import useTrackVulnerabilityJourney from "../../middleware/analytics/hooks/useTrackVulnerabilityJourney";

import {
  PSRServicesSelectionDataTest,
  defaultSelectedPSRServices,
} from "./constants";

interface SelectedPSRServices {
  quarterlyMeterRead: boolean;
  billInAudio: boolean;
  billInLargePrint: boolean;
  billInBraille: boolean;
  sendKnobTurner: boolean;
  passPhrase: boolean;
  none: boolean;
}

const PSRServicesSelection = () => {
  const { trackVulnerabilityJourneyOpen } = useTrackVulnerabilityJourney();
  const navigate = useNavigate();
  const { answers, setAnswers } = useVulnerabilityContext();
  const [selectedServices, setSelectedServices] = useState<SelectedPSRServices>(
    defaultSelectedPSRServices,
  );
  const [passphrase, setPassphrase] = useState("");
  const [disableNext, setDisableNext] = useState(false);
  const { setAuthenticated, authenticated } = useAuthContext();

  useEffect(() => {
    trackVulnerabilityJourneyOpen(PATHS.PSRServices, `3 of 3`);
  }, []);

  const onChangePassphrase = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setPassphrase(event.target.value);

  const onChange = (value: string, checked: boolean) => {
    if (checked) {
      if (value === "none") {
        setSelectedServices({
          ...defaultSelectedPSRServices,
          none: true,
        });
        setPassphrase("");
      } else {
        setSelectedServices((prev) => ({
          ...prev,
          [value]: true,
          none: false,
        }));
      }
    } else {
      const removeSelectedService = { ...selectedServices };
      delete removeSelectedService[value as keyof SelectedPSRServices];
      setSelectedServices(removeSelectedService);
      if (value === "passPhrase") {
        setPassphrase("");
      }
    }
  };

  const handleContinue = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { passPhrase, none, ...restServices } = selectedServices;
    setAnswers({
      ...answers,
      psr: {
        psrOutcome: { eligible: true, optedIn: true },
        psrTemporaryNeedsCodes: { ...answers.psr?.psrTemporaryNeedsCodes },
        psrServices: {
          ...restServices,
          ...(passPhrase ? { passPhrase: passphrase } : undefined),
        },
      },
    });

    navigate(`/${PATHS.PSRSummary}`);
  };

  const isValid = !!passphrase && passphrase.length <= 8;
  const isInvalid = passphrase.length > 8;

  const validationStatus = isValid
    ? "valid"
    : isInvalid
      ? "invalid"
      : undefined;
  const validationText = isValid
    ? "Your input is valid"
    : isInvalid
      ? "Must be between 1-8 characters"
      : undefined;

  const serviceOptions = [
    {
      key: "quarterlyMeterRead",
      title: "Quarterly meter read on your behalf",
      text: "If you don’t already have a smart meter and you, or anyone living with you, is unable to read your electricity or gas meters, you can request that we read your meter once every quarter.",
    },
    {
      key: "billInBraille",
      title: "Provide you a bill in braille format",
      text: "If you are registered blind or partially sighted, we can send large print or Braille bills in addition to your standard bill.",
    },
    {
      key: "billInLargePrint",
      title: "Provide a large printed bill",
      text: "If you are registered blind or partially sighted, we can send large print or Braille bills in addition to your standard bill.",
    },
    {
      key: "billInAudio",
      title: "Provide an audio CD bill",
      text: "We can supply Audio CD invoices to our PSR customers who are blind or partially sighted.",
    },
    {
      key: "sendKnobTurner",
      title: "Send you a contour knob turner",
      text: "If you find it difficult to use a particular appliance, the Contour Knob Turner is designed to make turning those difficult-to-grip, fiddly little knobs far easier and far less frustrating.",
    },
    {
      key: "passPhrase",
      title: "Pass phrase to identify us",
      text: "A pass phrase can be set for when meter readers, staff, and representatives need to visit your property.",
      component: (
        <Box
          style={{
            display: "none",
            opacity: 0,
            height: 0,

            ...(selectedServices.passPhrase && {
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              opacity: 1,
              height: "auto",
              transition: "opacity 700ms ease-in",
            }),
          }}
          data-testid={PSRServicesSelectionDataTest.PASSPHRASE_INPUT_CONTAINER}
        >
          <BodyText>
            If you would like to set up a PSR pass phrase please print it here:
            (Max 8 Characters).
          </BodyText>
          <TextInput
            label="Pass phrase"
            id="password"
            required
            value={passphrase}
            onChange={onChangePassphrase}
            validationStatus={validationStatus}
            validationText={validationText}
            data-testid={PSRServicesSelectionDataTest.PASSPHRASE_INPUT}
          />
        </Box>
      ),
    },
    { key: "none", title: "I don’t require any additional services" },
  ];
  useEffect(() => {
    if (
      /* disable button:
       * if pass phrase checkbox is selected and pass phrase not entered
       * if pass phrase is entered and pass phrase checkbox not selected
       * if no option is selected
       */
      (selectedServices.passPhrase && !passphrase.length) ||
      (passphrase.length && !selectedServices.passPhrase) ||
      Object.values(selectedServices).every((s) => !s)
    ) {
      setDisableNext(true);
    } else {
      setDisableNext(false);
    }
  }, [selectedServices, passphrase]);

  useEffect(() => {
    checkTokenExpiryOnInterval(authenticated, setAuthenticated);
  }, []);

  return (
    <Flex direction="column" marginTop="150">
      {!authenticated ? (
        <Unauthenticated />
      ) : (
        <Flex direction="column" gap="500">
          <Flex direction="column" gap="175">
            <Heading size="lg">
              Select the additional services you require:
            </Heading>

            <CheckboxGroup contentWidth="100%">
              {serviceOptions.map((opt) => (
                <CheckboxTile
                  key={opt.key}
                  label={
                    <div>
                      <BodyText
                        data-testid={`${PSRServicesSelectionDataTest.SERVICE_OPTION_LABEL_TITLE}_${opt.key}`}
                      >
                        {opt.title}
                      </BodyText>
                      <BodyText>{opt.text}</BodyText>

                      {opt.component ? opt.component : null}
                    </div>
                  }
                  value={opt.key}
                  checked={Boolean(
                    selectedServices[opt.key as keyof SelectedPSRServices],
                  )}
                  data-testid={`${PSRServicesSelectionDataTest.SERVICES_CHECKBOX}_${opt.key}`}
                  onCheckedChange={(checked) => onChange(opt.key, checked)}
                />
              ))}
            </CheckboxGroup>
          </Flex>

          <NavigationButtons
            onBack={() => navigate(-1)}
            onContinue={handleContinue}
            disableNext={disableNext}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default PSRServicesSelection;
